"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";
import { Button } from "../../components/ui/button";
import InputForm from "@/components/Input/InputForm";
import SelectField from "@/components/Input/SelectForm";

import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { AlertCircle, User } from "lucide-react";
import { formatRut, validateRut, parseRut } from "../../lib/rut-validator";
import {
  saveBeneficiario,
  saveModifiedApiBeneficiario,
  getTotalPercentageByPoliza,
  rutExistsInPoliza,
  type ApiBeneficiarioModification,
} from "../../lib/beneficiarios-storage";
import {
  apiClient,
  type ApiBeneficiario,
  type Region,
  type Ciudad,
  type Comuna,
} from "../lib/api";
import { FormProvider, useForm } from "react-hook-form";
import {
  BeneficiarioFormZod,
  BeneficiarioSchema,
} from "@/schema/BeneficiarioSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateAddedBeneficiario } from "@/lib/beneficiarios-storage";

type FieldErrors = Record<string, string | undefined>;

interface BeneficiarioModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  numeroPoliza: number;
  onSuccess: () => void;
  editMode?: boolean;
  existingBeneficiario?: ApiBeneficiario;
  apiBeneficiarios?: ApiBeneficiario[];
  rutContratante?: string;
}

export function BeneficiarioModalForm({
  open,
  onOpenChange,
  numeroPoliza,
  onSuccess,
  editMode = false,
  existingBeneficiario,
  apiBeneficiarios = [],
  rutContratante,
}: BeneficiarioModalProps) {
  const methods = useForm<BeneficiarioFormZod>({
    resolver: zodResolver(BeneficiarioSchema),
    defaultValues: {
      tipoBeneficiario: undefined,
      rut: "",
      nombre: "",
      apellidoPaterno: "",
      apellidoMaterno: "",
      fechaNacimiento: "",
      sexo: "",
      parentesco: undefined,
      email: "",
      region: "",
      ciudad: "",
      comuna: "",
      direccion: "",
      casaDepto: "",
      celular: "",
      accion: "AGREGAR",
      porcentaje: "",
    },
  });
  const [generalError, setGeneralError] = useState("");
  const [currentPercentage, setCurrentPercentage] = useState(0);

  const [regiones, setRegiones] = useState<Region[]>([]);
  const [ciudades, setCiudades] = useState<Ciudad[]>([]);
  const [comunas, setComunas] = useState<Comuna[]>([]);
  const [loadingRegiones, setLoadingRegiones] = useState(false);
  const [loadingCiudades, setLoadingCiudades] = useState(false);
  const [loadingComunas, setLoadingComunas] = useState(false);
  const [locationDataReady, setLocationDataReady] = useState(false);

  const regionWatch = methods.watch("region");
  const ciudadWatch = methods.watch("ciudad");
  const { setValue } = methods;

  const isInitialLoadRef = useRef(false);

  const [formData, setFormData] = useState({
    tipoBeneficiario: "",
    rut: "",
    nombre: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    fechaNacimiento: "",
    sexo: "",
    parentesco: "",
    email: "",
    region: "",
    ciudad: "",
    comuna: "",
    direccion: "",
    casaDepto: "",
    celular: "",
    accion: "AGREGAR",
    porcentaje: "",
  });

  useEffect(() => {
    if (open) {
      setLocationDataReady(false);
      loadRegiones();
    } else {
      setRegiones([]);
      setCiudades([]);
      setComunas([]);
      setLocationDataReady(false);
      isInitialLoadRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (formData.region && open && !isInitialLoadRef.current) {
      const idRegion = Number.parseInt(formData.region);
      if (!Number.isNaN(idRegion)) {
        setLocationDataReady(false);

        setFormData((prev) => ({ ...prev, ciudad: "", comuna: "" }));
      }
    } else if (!formData.region && !isInitialLoadRef.current && open) {
      setCiudades([]);
      setComunas([]);
      setLocationDataReady(false);
      setFormData((prev) => ({ ...prev, ciudad: "", comuna: "" }));
    }
  }, [formData.region, open]);

  useEffect(() => {
    const fetchCiudades = async () => {
      if (regionWatch && !editMode) {
        setLoadingCiudades(true);

        try {
          const response = await apiClient.getCiudades(
            parseInt(regionWatch.toString())
          );
          if (response.success && response.data) {
            setCiudades(response.data);
          }
        } catch (error) {
          setCiudades([]);
        } finally {
          setLoadingCiudades(false);
        }
      } else if (editMode && existingBeneficiario && regionWatch) {
        try {
          const response = await apiClient.getCiudades(
            parseInt(regionWatch.toString())
          );
          if (response.success && response.data) {
            setCiudades(response.data);
          }
        } catch (error) {
          setCiudades([]);
        } finally {
          setLoadingCiudades(false);
        }
      } else {
        setCiudades([]);
        setComunas([]);
      }
    };

    fetchCiudades();
  }, [regionWatch, methods]);

  useEffect(() => {
    const fetchComunas = async () => {
      if (ciudadWatch && !editMode) {
        setLoadingComunas(true);
        // Resetear comuna

        try {
          const response = await apiClient.getComunas(
            parseInt(regionWatch.toString())
          );
          if (response.success && response.data) {
            setComunas(response.data);
          }
        } catch (error) {
          setComunas([]);
        } finally {
          setLoadingComunas(false);
        }
      } else if (editMode && existingBeneficiario && ciudadWatch) {
        try {
          const response = await apiClient.getComunas(
            parseInt(regionWatch.toString())
          );
          if (response.success && response.data) {
            setComunas(response.data);
          }
        } catch (error) {
          setComunas([]);
        } finally {
          setLoadingComunas(false);
        }
      } else {
        setComunas([]);
      }
    };

    fetchComunas();
  }, [ciudadWatch, methods]);

  const loadRegiones = async () => {
    setLoadingRegiones(true);
    try {
      const response = await apiClient.getRegiones();
      if (response.success && response.data) {
        setRegiones(response.data);
      }
    } catch (error) {
    } finally {
      setLoadingRegiones(false);
    }
  };

  const loadCiudades = async (idRegion: number) => {
    setLoadingCiudades(true);

    try {
      const response = await apiClient.getCiudades(idRegion);
      if (response.success && response.data) {
        setCiudades(response.data);
      } else {
        setCiudades([]);
      }
    } catch (error) {
      setCiudades([]);
    } finally {
      setLoadingCiudades(false);
    }
  };

  const loadComunas = async (idRegion: number) => {
    setLoadingComunas(true);
    try {
      const response = await apiClient.getComunas(idRegion);
      if (response.success && response.data) {
        setComunas(response.data);
      } else {
        setComunas([]);
      }
    } catch (error) {
      setComunas([]);
    } finally {
      setLoadingComunas(false);
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    } else if (open && !editMode) {
      resetForm();
    }
  }, [open, editMode]);

  useEffect(() => {
    const loadExistingBeneficiarioData = async () => {
      if (open && editMode && existingBeneficiario && regiones.length > 0) {
        isInitialLoadRef.current = true;
        setLocationDataReady(false);

        const rutFormatted = formatRut(
          `${existingBeneficiario.rut}${existingBeneficiario.dv}`
        );

        const parentescoMap: Record<string, string> = {
          Cónyuge: "5",
          "Hijo/a": "6",
          Padre: "7",
          Madre: "8",
          "Hermano/a": "9",
          Otro: "10",
        };

        const parentescoValue =
          typeof existingBeneficiario.parentesco === "string"
            ? parentescoMap[existingBeneficiario.parentesco] || "5"
            : existingBeneficiario.parentesco.toString();

        const beneficiarioData = existingBeneficiario as any;
        const regionValue = beneficiarioData.idRegion
          ? beneficiarioData.idRegion.toString()
          : beneficiarioData.region
          ? beneficiarioData.region.toString()
          : 0;
        const ciudadValue = beneficiarioData.idCiudad
          ? beneficiarioData.idCiudad.toString()
          : beneficiarioData.ciudad
          ? beneficiarioData.ciudad.toString()
          : 0;
        const comunaValue = beneficiarioData.idComuna
          ? beneficiarioData.idComuna.toString()
          : beneficiarioData.comuna
          ? beneficiarioData.comuna.toString()
          : 0;

        if (regionValue) {
          const idRegion = Number.parseInt(regionValue);
          if (!Number.isNaN(idRegion)) {
            omunaValue;
            // );
            await Promise.all([loadCiudades(idRegion), loadComunas(idRegion)]);
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        setValue(
          "tipoBeneficiario",
          existingBeneficiario.tipoBeneficiario?.toString() || "4"
        );
        setValue("rut", rutFormatted);
        setValue("nombre", existingBeneficiario.nombre);
        setValue("apellidoPaterno", existingBeneficiario.apellidoPaterno);
        setValue("apellidoMaterno", existingBeneficiario.apellidoMaterno);
        setValue(
          "fechaNacimiento",
          existingBeneficiario.fechaNacimiento?.split("T")[0] || ""
        );
        setValue(
          "sexo",
          existingBeneficiario.sexo ? existingBeneficiario.sexo.toString() : ""
        );
        setValue("parentesco", parentescoValue);
        setValue("email", existingBeneficiario.email || "");
        setValue("region", regionValue);
        setValue("ciudad", ciudadValue);
        setValue("comuna", comunaValue);
        setValue("direccion", existingBeneficiario.direccion || "");
        setValue("casaDepto", existingBeneficiario.casaDepto || "");
        setValue("celular", existingBeneficiario.celular || "");
        setValue("accion", "MODIFICAR");
        setValue(
          "porcentaje",
          existingBeneficiario.porcentaje?.toString() || ""
        );

        setLocationDataReady(true);

        setTimeout(() => {
          isInitialLoadRef.current = false;
        }, 500);
      }
    };

    loadExistingBeneficiarioData();
  }, [open, editMode, existingBeneficiario, regiones]);

  //   const checkRequiredFields = () => {
  //     console.log("=== CAMPOS REQUERIDOS ===");
  //     const values = methods.getValues();

  //     const requiredFields = [
  //       "tipoBeneficiario",
  //       "rut",
  //       "nombre",
  //       "apellidoPaterno",
  //       "apellidoMaterno",
  //       "fechaNacimiento",
  //       "parentesco",
  //       "email",
  //       "sexo",
  //       "region",
  //       "ciudad",
  //       "comuna",
  //       "celular",
  //       "porcentaje",
  //     ];

  //     requiredFields.forEach((field) => {
  //       const value = values[field];
  //       console.log(`${field}:`, value, value ? "✅" : "❌ VACÍO");
  //     });

  //     console.log("=========================");
  //   };

  useEffect(() => {
    const excludeId =
      editMode && existingBeneficiario ? existingBeneficiario.id : undefined;
    const total = getTotalPercentageByPoliza(
      numeroPoliza,
      apiBeneficiarios,
      excludeId
    );

    setCurrentPercentage(total);

    if (total === 0 && !editMode) {
      setFormData((prev) => ({ ...prev, porcentaje: "100" }));
    }

    if (total >= 100 && !editMode) {
      setGeneralError(
        `Esta póliza ya tiene el 100% distribuido entre sus beneficiarios. No puede agregar más beneficiarios.`
      );
    } else {
      setGeneralError("");
    }
  }, [open, numeroPoliza, editMode, existingBeneficiario, apiBeneficiarios]);

  const resetForm = () => {
    methods.reset({
      tipoBeneficiario: undefined,
      rut: "",
      nombre: "",
      apellidoPaterno: "",
      apellidoMaterno: "",
      fechaNacimiento: "",
      parentesco: undefined,
      email: "",
      sexo: "",
      region: "",
      ciudad: "",
      comuna: "",
      direccion: "",
      casaDepto: "",
      celular: "",
      accion: "AGREGAR",
      porcentaje: "",
    });

    setCiudades([]);
    setComunas([]);

    setGeneralError("");
    isInitialLoadRef.current = false;
    setLocationDataReady(false);
  };

  const onSubmit = (dataBeneficiario: BeneficiarioFormZod) => {
    // console.log(dataBeneficiario);
    const dataToSend = {
      ...dataBeneficiario,
      tipoBeneficiario: Number(dataBeneficiario.tipoBeneficiario),
      parentesco: Number(dataBeneficiario.parentesco),
      sexo: Number(dataBeneficiario.sexo),
      porcentaje: dataBeneficiario.porcentaje,
      region: Number(dataBeneficiario.region),
      ciudad: Number(dataBeneficiario.ciudad),
      comuna: Number(dataBeneficiario.comuna),
    };

    setGeneralError("");

    const isAddedBeneficiary =
      existingBeneficiario?.id?.toString().includes("replicated") ||
      existingBeneficiario?.id?.toString().includes("temp") ||
      existingBeneficiario?.id?.toString().includes("added");

    if (currentPercentage >= 100 && !editMode) {
      setGeneralError(
        `Esta póliza ya tiene el 100% distribuido. No puede agregar más beneficiarios.`
      );
      return;
    }

    const errors: FieldErrors = {};

    if (!validateRut(dataToSend.rut)) {
      errors.rut = "RUT inválido. Formato: 12.345.678-9";
    } else {
      const parsed = parseRut(dataToSend.rut);
      if (!parsed) {
        errors.rut = "Error al procesar el RUT";
      } else {
        const excludeId =
          editMode && existingBeneficiario
            ? existingBeneficiario.id
            : undefined;
        if (
          rutExistsInPoliza(
            numeroPoliza,
            parsed.rut,
            excludeId,
            apiBeneficiarios
          )
        ) {
          errors.rut = `Este RUT ya está registrado en esta póliza`;
        }
      }
    }

    if (!dataToSend.fechaNacimiento) {
      errors.fechaNacimiento = "La fecha de nacimiento es requerida";
    } else {
      const selectedDate = new Date(dataToSend.fechaNacimiento);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        errors.fechaNacimiento = "La fecha de nacimiento no puede ser futura";
      }
    }

    if (!dataToSend.porcentaje) {
      errors.porcentaje = "El porcentaje es requerido";
    } else {
      const percentage = Number.parseInt(dataToSend.porcentaje.toString());
      if (percentage <= 0 || percentage > 100) {
        errors.porcentaje = "El porcentaje debe estar entre 1 y 100";
      } else {
        const totalAfterAdd = currentPercentage + percentage;
        if (totalAfterAdd > 100) {
          errors.porcentaje = `Excede el 100%. Máximo disponible: ${
            100 - currentPercentage
          }%`;
        }
      }
    }

    const parsed = parseRut(dataToSend.rut);
    if (!parsed) {
      return;
    }

    if (editMode && existingBeneficiario) {
      if (isAddedBeneficiary) {
        updateAddedBeneficiario(existingBeneficiario.id.toString(), {
          id: existingBeneficiario.id,
          tipoBeneficiario: dataToSend.tipoBeneficiario,
          rut: parsed.rut,
          dv: parsed.dv,
          nombre: dataToSend.nombre.trim(),
          apellidoPaterno: dataToSend.apellidoPaterno.trim(),
          apellidoMaterno: dataToSend.apellidoMaterno.trim(),
          fechaNacimiento: dataToSend.fechaNacimiento,
          sexo: dataToSend.sexo,
          parentesco: dataToSend.parentesco,
          email: dataToSend.email.trim(),
          region: dataToSend.region,
          ciudad: dataToSend.ciudad,
          comuna: dataToSend.comuna,
          direccion: dataToSend.direccion.trim(),
          casaDepto: dataToSend.casaDepto.trim(),
          celular: dataToSend.celular.trim(),
          porcentaje: Number.parseInt(dataToSend.porcentaje.toString()),
          hasBeenModified: true, // ← MARCAR COMO MODIFICADO
        });
      } else {
        const modifiedBeneficiario: ApiBeneficiarioModification = {
          id: existingBeneficiario.id,
          numeroPoliza,
          tipoBeneficiario: dataToSend.tipoBeneficiario,
          rut: parsed.rut,
          dv: parsed.dv,
          nombre: dataToSend.nombre.trim(),
          apellidoPaterno: dataToSend.apellidoPaterno.trim(),
          apellidoMaterno: dataToSend.apellidoMaterno.trim(),
          fechaNacimiento: dataToSend.fechaNacimiento,
          sexo: dataToSend.sexo,
          parentesco: dataToSend.parentesco,
          email: dataToSend.email.trim(),
          region: dataToSend.region,
          ciudad: dataToSend.ciudad,
          comuna: dataToSend.comuna,
          direccion: dataToSend.direccion.trim(),
          casaDepto: dataToSend.casaDepto.trim(),
          celular: dataToSend.celular.trim(),
          porcentaje: Number.parseInt(dataToSend.porcentaje.toString()),
        };

        saveModifiedApiBeneficiario(modifiedBeneficiario);
      }
    } else if (!editMode && !existingBeneficiario) {
      saveBeneficiario({
        id: `${Date.now()}-${Math.random()}-added`,
        numeroPoliza,
        tipoBeneficiario: dataToSend.tipoBeneficiario,
        rut: parsed.rut,
        dv: parsed.dv,
        nombre: dataToSend.nombre.trim(),
        apellidoPaterno: dataToSend.apellidoPaterno.trim(),
        apellidoMaterno: dataToSend.apellidoMaterno.trim(),
        fechaNacimiento: dataToSend.fechaNacimiento,
        sexo: dataToSend.sexo,
        parentesco: dataToSend.parentesco,
        email: dataToSend.email.trim(),
        region: dataToSend.region,
        ciudad: dataToSend.ciudad,
        comuna: dataToSend.comuna,
        direccion: dataToSend.direccion.trim(),
        casaDepto: dataToSend.casaDepto.trim(),
        celular: dataToSend.celular.trim(),
        accion: dataToSend.accion,
        porcentaje: Number.parseInt(dataToSend.porcentaje.toString()),
        hasBeenModified: false,
      });
    }

    resetForm();
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--color-z-blue)] sm:text-lg">
            <User className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--color-z-blue)]" />
            {editMode ? "Editar Beneficiario" : "Agregar Beneficiario"}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {editMode
              ? "Modifica los datos del beneficiario"
              : "Complete los datos del beneficiario"}{" "}
            para la póliza N° {numeroPoliza}
            {rutContratante && <p> Rut contratante: {rutContratante}</p>}
            <span className="block mt-2 font-medium text-[var(--color-z-blue)]">
              Porcentaje actual: {currentPercentage}% | Disponible:{" "}
              {100 - currentPercentage}%
            </span>
          </DialogDescription>
        </DialogHeader>

        {generalError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{generalError}</AlertDescription>
          </Alert>
        )}
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="space-y-4 sm:space-y-6"
          >
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
              <div className="space-y-2">
                <SelectField
                  icon="users"
                  name="tipoBeneficiario"
                  label="Tipo de Beneficiario"
                  placeholder="Seleccione tipo"
                  optionsNumber={[
                    { value: 4, label: "Beneficiario" },
                    { value: 3, label: "Beneficiario contingente" },
                  ]}
                ></SelectField>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="idcard"
                  name="rut"
                  type="text"
                  label="Rut"
                  placeholder="20.953.232-9"
                  transform={formatRut}
                  minLength={2}
                  maxLength={12}
                  required
                >
                  Rut
                </InputForm>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="user"
                  name="nombre"
                  type="text"
                  label="nombre"
                  placeholder="Yakob"
                  required
                >
                  Nombre
                </InputForm>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="user"
                  name="apellidoPaterno"
                  type="text"
                  label="apellidoPaterno"
                  placeholder="Perez"
                  required
                >
                  Apellido Paterno
                </InputForm>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="user"
                  name="apellidoMaterno"
                  type="text"
                  label="apellidoMaterno"
                  placeholder="Martinez"
                  required
                >
                  Apellido Materno
                </InputForm>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="calendario"
                  name="fechaNacimiento"
                  type="date"
                  label="fechaNacimiento"
                  placeholder="Martinez"
                  required
                >
                  Fecha de Nacimiento{" "}
                </InputForm>
              </div>

              <div className="space-y-2">
                <SelectField
                  icon="users"
                  name="parentesco"
                  label="Parentesco"
                  placeholder="Seleccione parentesco"
                  optionsNumber={[
                    { value: 5, label: "Cónyuge" },
                    { value: 6, label: "Hijo/a" },
                    { value: 7, label: "Padre" },
                    { value: 8, label: "Madre" },
                    { value: 9, label: "Hermano" },
                    { value: 10, label: "Otro" },
                  ]}
                ></SelectField>
              </div>
              <div className="space-y-2">
                <SelectField
                  icon="user"
                  name="sexo"
                  label="Sexo"
                  placeholder="Seleccione Sexo"
                  optionsNumber={[
                    { value: 1, label: "Masculino" },
                    { value: 2, label: "Femenino" },
                  ]}
                ></SelectField>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="email"
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="juancito@zurich.com"
                  required
                >
                  Email
                </InputForm>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="fono"
                  name="celular"
                  type="text"
                  label="celular"
                  placeholder="912345678"
                  minLength={2}
                  maxLength={9}
                  required
                >
                  Celular
                </InputForm>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="porcentaje"
                  name="porcentaje"
                  type="number"
                  label="porcentaje"
                  placeholder="%"
                  min={"1"}
                  max={"100"}
                  required
                >
                  Porcentaje (%)
                </InputForm>
              </div>

              <div className="space-y-2">
                <SelectField
                  icon="mapa"
                  name="region"
                  label="Región"
                  placeholder="Seleccione región"
                  optionsNumber={regiones.map((region) => ({
                    value: region.idRegion,
                    label: region.nombre,
                  }))}
                />
              </div>

              <div className="space-y-2">
                <SelectField
                  icon="mapa"
                  name="ciudad"
                  label="Ciudad"
                  placeholder={
                    !regionWatch
                      ? "Seleccione región primero"
                      : loadingCiudades
                      ? "Cargando ciudades..."
                      : "Seleccione ciudad"
                  }
                  optionsNumber={
                    ciudades.length === 0 && regionWatch && !loadingCiudades
                      ? [
                          {
                            value: 0,
                            label: "No hay ciudades disponibles",
                          },
                        ]
                      : ciudades.map((ciudad) => ({
                          value: ciudad.idCiudad,
                          label: ciudad.nombre,
                        }))
                  }
                  disabled={!regionWatch || loadingCiudades}
                />
              </div>

              <div className="space-y-2">
                <SelectField
                  name="comuna"
                  label="Comuna"
                  icon="mapa"
                  placeholder={
                    !ciudadWatch
                      ? "Seleccione ciudad primero"
                      : loadingComunas
                      ? "Cargando comunas..."
                      : "Seleccione comuna"
                  }
                  optionsNumber={
                    comunas.length === 0 && ciudadWatch && !loadingComunas
                      ? [
                          {
                            value: 0,
                            label: "No hay comunas disponibles",
                          },
                        ]
                      : comunas.map((comuna) => ({
                          value: comuna.idComuna,
                          label: comuna.nombre,
                        }))
                  }
                  disabled={!ciudadWatch || loadingComunas}
                />
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="mapa"
                  name="direccion"
                  type="text"
                  label="direccion"
                  placeholder="Pasaje 2"
                  required
                >
                  Dirección
                </InputForm>
              </div>

              <div className="space-y-2">
                <InputForm
                  icon="mapa"
                  name="casaDepto"
                  type="text"
                  label="casaDepto"
                  placeholder="Casa 2"
                  required
                >
                  Casa/Depto
                </InputForm>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button
                type="submit"
                className="w-full sm:w-auto bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)]"
                disabled={currentPercentage >= 100 && !editMode}
              >
                {editMode ? "Guardar Cambios" : "Guardar Beneficiario"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto bg-transparent"
                onClick={() => {
                  resetForm();
                  onOpenChange(false);
                }}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
